import { useCallback, useEffect, useState } from "react";
import { BASE_API_URL } from "../../Components/config";

const API = `${BASE_API_URL}/api/v1/course-segments`;

const authHeaders = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
});

const slug = (name) =>
  (name || "").trim().toUpperCase().replace(/[^A-Z0-9]+/g, "-").slice(0, 32) || "TOPIC";

/**
 * The course-content tree, plus every write a teacher makes while building it.
 *
 * Every mutation re-reads the tree rather than patching state locally: the
 * server owns positions, and a create or a reorder changes more rows than the
 * one that was touched.
 */
export function useCourseContent(courseGuid) {
  const [modules, setModules] = useState(null); // null = still loading
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    if (!courseGuid) return;
    try {
      const res = await fetch(`${API}/by-course-guid/with-children/${courseGuid}`, {
        headers: authHeaders(),
      });
      if (!res.ok) throw new Error(`load failed (${res.status})`);
      const data = await res.json();
      setModules(Array.isArray(data) ? data : []);
      setError(null);
    } catch (e) {
      console.error(e);
      setModules([]);
      setError("Could not load the course content.");
    }
  }, [courseGuid]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const run = async (fn) => {
    setBusy(true);
    try {
      const out = await fn();
      await refresh();
      return out;
    } finally {
      setBusy(false);
    }
  };

  /** A module is a top-level segment; a lesson is one nested under it. */
  const addModule = (name) =>
    run(async () => {
      const res = await fetch(`${API}/`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ course_guid: courseGuid, name, code: slug(name), kind: "CONTENT" }),
      });
      if (!res.ok) throw new Error("create module failed");
      return res.json();
    });

  const addLesson = (moduleGuid, name) =>
    run(async () => {
      const res = await fetch(`${API}/`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({
          course_guid: courseGuid,
          parent_course_segment_guid: moduleGuid,
          name,
          code: slug(name),
          kind: "CONTENT",
        }),
      });
      if (!res.ok) throw new Error("create lesson failed");
      return res.json();
    });

  const rename = (guid, name) =>
    run(async () => {
      const res = await fetch(`${API}/${guid}`, {
        method: "PUT",
        headers: authHeaders(),
        body: JSON.stringify({ name, code: slug(name) }),
      });
      if (!res.ok) throw new Error("rename failed");
    });

  const saveBody = (guid, overviewHtml) =>
    run(async () => {
      const res = await fetch(`${API}/${guid}`, {
        method: "PUT",
        headers: authHeaders(),
        body: JSON.stringify({ overview_html: overviewHtml }),
      });
      if (!res.ok) throw new Error("save failed");
    });

  const remove = (guid) =>
    run(async () => {
      const res = await fetch(`${API}/${guid}`, { method: "DELETE", headers: authHeaders() });
      if (!res.ok) throw new Error("delete failed");
    });

  /** Positions are sent for the whole affected list, because moving one item
   *  renumbers its neighbours too. */
  const reorder = (items) =>
    run(async () => {
      const res = await fetch(`${API}/reorder`, {
        method: "PUT",
        headers: authHeaders(),
        body: JSON.stringify({ course_guid: courseGuid, items }),
      });
      if (!res.ok) throw new Error("reorder failed");
    });

  const upload = (guid, files) =>
    run(async () => {
      const form = new FormData();
      // The server reads them from "attachments".
      [...files].forEach((f) => form.append("attachments", f));
      const res = await fetch(`${API}/${guid}/attachments`, {
        method: "POST",
        headers: { Authorization: `Bearer ${localStorage.getItem("accessToken")}` },
        body: form,
      });
      if (!res.ok) throw new Error("upload failed");
    });

  const removeAttachment = (attachmentGuid) =>
    run(async () => {
      const res = await fetch(`${API}/attachments/${attachmentGuid}`, {
        method: "DELETE",
        headers: authHeaders(),
      });
      if (!res.ok) throw new Error("attachment delete failed");
    });

  return {
    modules,
    error,
    busy,
    refresh,
    addModule,
    addLesson,
    rename,
    saveBody,
    remove,
    reorder,
    upload,
    removeAttachment,
  };
}
